import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Database,
  Key,
  Shield,
  Server,
  Link,
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { useSystemConfig, useApiKeyStatus } from '../hooks';

type TabType = 'general' | 'api-keys' | 'database' | 'security';

const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTabState] = useState<TabType>(tabFromUrl || 'general');
  const setActiveTab = (tab: TabType) => {
    setActiveTabState(tab);
    setSearchParams(tab !== 'general' ? { tab } : {}, { replace: true });
  };
  const { data: systemConfig, isLoading: configLoading, error: configError } = useSystemConfig();
  const { data: apiKeyStatus, isLoading: keysLoading } = useApiKeyStatus();

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'general', label: 'General', icon: <SettingsIcon size={16} /> },
    { id: 'api-keys', label: 'API Keys', icon: <Key size={16} /> },
    { id: 'database', label: 'Database', icon: <Database size={16} /> },
    { id: 'security', label: 'Security', icon: <Shield size={16} /> },
  ];

  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-slate-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center text-red-500">
          <AlertCircle size={40} className="mx-auto mb-4" />
          <p>Failed to load settings</p>
          <p className="text-sm text-slate-500 mt-2">
            {configError instanceof Error ? configError.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">Settings</h2>
        <p className="text-slate-500">System configuration and status (read-only)</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* General Tab */}
          {activeTab === 'general' && systemConfig && (
            <div className="space-y-6">
              <section className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Server size={20} /> Environment
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <ConfigItem label="App Name" value={systemConfig.environment.app_name} />
                  <ConfigItem
                    label="Environment"
                    value={systemConfig.environment.environment}
                    badge={
                      systemConfig.environment.environment === 'production'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }
                  />
                  <ConfigItem
                    label="Debug Mode"
                    value={systemConfig.environment.debug ? 'Enabled' : 'Disabled'}
                    status={!systemConfig.environment.debug}
                  />
                  <ConfigItem label="Log Level" value={systemConfig.environment.log_level.toUpperCase()} />
                </div>
              </section>

              <div className="h-px bg-slate-100" />

              <section className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Link size={20} /> Services
                </h3>
                <div className="space-y-3">
                  <ConfigItem label="Agent Server" value={systemConfig.services.agent_server_url} fullWidth />
                  <ConfigItem
                    label="LangFuse"
                    value={systemConfig.langfuse.host}
                    status={systemConfig.langfuse.enabled}
                    fullWidth
                  />
                </div>
              </section>
            </div>
          )}

          {/* API Keys Tab */}
          {activeTab === 'api-keys' && (
            <div className="space-y-6">
              <section className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Key size={20} /> API Keys Status
                </h3>
                {keysLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={24} className="animate-spin text-slate-400" />
                  </div>
                ) : apiKeyStatus ? (
                  <div className="space-y-4">
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-slate-700">LangFuse</span>
                        <StatusBadge configured={apiKeyStatus.langfuse.configured} />
                      </div>
                      <div className="space-y-2">
                        <KeyDisplay label="Public Key" value={apiKeyStatus.langfuse.public_key} />
                        <KeyDisplay label="Secret Key" value={apiKeyStatus.langfuse.secret_key} />
                      </div>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-slate-700">JWT Authentication</span>
                        <StatusBadge configured={apiKeyStatus.jwt.configured} />
                      </div>
                      <div className="space-y-2">
                        <KeyDisplay label="Algorithm" value={apiKeyStatus.jwt.algorithm} />
                        <KeyDisplay label="Secret Key" value={apiKeyStatus.jwt.secret_key} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <AlertCircle size={24} className="mx-auto mb-2" />
                    <p>Unable to load API key status</p>
                  </div>
                )}
              </section>
            </div>
          )}

          {/* Database Tab */}
          {activeTab === 'database' && systemConfig && (
            <div className="space-y-6">
              <section className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Database size={20} /> Database Configuration
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <ConfigItem label="Host" value={systemConfig.database.host} />
                  <ConfigItem label="Port" value={systemConfig.database.port.toString()} />
                  <ConfigItem label="Database" value={systemConfig.database.database} />
                  <ConfigItem label="Pool Size" value={systemConfig.database.pool_size.toString()} />
                  <ConfigItem label="Max Overflow" value={systemConfig.database.max_overflow.toString()} />
                </div>
              </section>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && systemConfig && (
            <div className="space-y-6">
              <section className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Shield size={20} /> Security Settings
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <ConfigItem
                      label="Access Token Expiry"
                      value={`${systemConfig.security.access_token_expire_minutes} minutes`}
                      icon={<Clock size={14} />}
                    />
                    <ConfigItem
                      label="Refresh Token Expiry"
                      value={`${systemConfig.security.refresh_token_expire_days} days`}
                      icon={<Clock size={14} />}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="text-xs font-semibold text-slate-500 uppercase mb-2 block">CORS Origins</label>
                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                      {systemConfig.security.cors_origins.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {systemConfig.security.cors_origins.map((origin, i) => (
                            <span
                              key={i}
                              className="bg-white px-2 py-1 rounded text-xs font-mono text-slate-600 border border-slate-200"
                            >
                              {origin}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-500 text-sm">All origins allowed</span>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface ConfigItemProps {
  label: string;
  value: string;
  badge?: string;
  status?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
}

const ConfigItem: React.FC<ConfigItemProps> = ({ label, value, badge, status, fullWidth, icon }) => (
  <div className={`p-4 border border-slate-200 rounded-lg ${fullWidth ? 'col-span-2' : ''}`}>
    <label className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1">
      {icon}
      {label}
    </label>
    <div className="flex items-center gap-2 mt-1">
      <span className="font-mono text-sm text-slate-700 break-all">{value}</span>
      {badge && <span className={`text-xs px-2 py-0.5 rounded ${badge}`}>{value}</span>}
      {status !== undefined &&
        (status ? (
          <CheckCircle size={16} className="text-green-500" />
        ) : (
          <XCircle size={16} className="text-red-500" />
        ))}
    </div>
  </div>
);

interface StatusBadgeProps {
  configured: boolean;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ configured }) => (
  <span
    className={`text-xs px-2 py-1 rounded ${configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
  >
    {configured ? 'Configured' : 'Not Configured'}
  </span>
);

interface KeyDisplayProps {
  label: string;
  value: string;
}

const KeyDisplay: React.FC<KeyDisplayProps> = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-mono text-slate-700 bg-white px-2 py-1 rounded border border-slate-200">{value}</span>
  </div>
);

export default Settings;
